import * as React from "react";
import { Box, AlertColor } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { CclPersistantDataGridMiscButton } from "../../../../components/common/cclPersistentDataGrid/cclPersistentDataGrid";
import CclDataGridToolbar from "../../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import ModifyWorkEntryDrawer from "./modifyWorkEntryDrawer";
import { WorkEntryFormValues } from "./modifyWorkEntryDrawer";
import TrainerEntryDrawer from "./trainerEntryDetail/trainerEntryDrawer";
import { cclTokenedPayrollApi } from "../../../../services/cclTokenedPayrollApi";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { AccessEventOFCReducedDetails } from "../../../../services/types/accessEventTypes";

export interface WorkEntryDataGridProps {
    sessionId: string;
    projectId: string;
    projectCode: string;
    revRecDate?: string | Date | null;
    projectName: string;
    displayWorkEntryDetails: (workEntryId: number) => void;
    loading?: boolean;
    onShowSnackbar: (message: string, severity: AlertColor) => void;
}

interface WorkEntryGridRow {
    id?: string | number;
    eventId?: string | number;
    imKey?: string | number;
    email?: string;
    projectKey?: string;
    workDate?: string | Date | null;
    payDate?: string | Date | null;
    audience?: string;
    type?: string;
    revRecDate?: string | Date | null;
    rev_rec_date?: string | Date | null;
    eventName?: string;
    eventNotes?: string;
    EventNotes?: string;
}

const toDate = (value: string | Date | null | undefined): Date | null => {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (match) {
        const localDate = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
        return Number.isNaN(localDate.getTime()) ? null : localDate;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const WorkEntryDataGrid: React.FC<WorkEntryDataGridProps> = (props) => {

    const [openModifyDrawer, setOpenModifyDrawer] = React.useState<boolean>(false);
    const [openTrainerDrawer, setOpenTrainerDrawer] = React.useState<boolean>(false);
    const [newEntry, setNewEntry] = React.useState<boolean>(true);
    const [selectedRows, setSelectedRows] = React.useState<Array<string | number>>([]);
    const [selectedWorkEntry, setSelectedWorkEntry] = React.useState<WorkEntryFormValues | undefined>(
        undefined,
    );

    const { data: workEntriesData, isLoading: workEntriesLoading } = cclTokenedPayrollApi.useGetWorkEntriesByProjectIdQuery(props.projectId);
    const [deleteWorkEntry] = cclTokenedPayrollApi.useDeleteWorkEntryMutation();
    const { logEvent } = useLogAccessEvent();

    const isExactlyOneSelected = selectedRows.length === 1;

    const WorkEntryColumns = [
        {
            field: 'eventId',
            type: "string",
            headerName: 'ID',
            width: 80,
        },
        {
            field: 'workDate',
            type: "date",
            headerName: 'Date Of Work',
            renderCell: (params: any) => {
                const dateValue = toDate(params.value);
                return dateValue ? dateValue.toLocaleDateString() : "";
            },
            width: 120
        },
        {
            field: "payDate",
            type: "date",
            headerName: "Payment Date",
            renderCell: (params: any) => {
                const dateValue = toDate(params.value);
                return dateValue ? dateValue.toLocaleDateString() : "";
            },
            width: 120
        },
        {
            field: "rev_rec_date",
            type: "date",
            headerName: "Rev. Rec. Date",
            renderCell: (params: any) => {
                const dateValue = toDate(params.value);
                return dateValue ? dateValue.toLocaleDateString() : "";
            },
            width: 120,
        },
        {
            field: "eventNotes",
            type: "string",
            headerName: "Notes",
            width: 200
        }
    ];

    const workEntryButtons: CclPersistantDataGridMiscButton[] = [
        {
            id: "add-work-entry",
            caption: "Add Work Entry",
            onClick: () => {},
            returnSelected: true,
            restricted: false,
            loadingBtn: true,

        },
        {
            id: "edit-work-entry",
            caption: "Edit Work Entry",
            onClick: () => {},
            returnSelected: true,
            restricted: false,
            disable: !isExactlyOneSelected,
        },
        {
            id: "delete-work-entry",
            caption: "Delete Work Entry",
            onClick: () => {},
            returnSelected: true,
            restricted: false,
            loadingBtn: true,
            disable: !isExactlyOneSelected,
        },
        {
            id: "view-trainer-entry",
            caption: "View Faculty Details",
            onClick: () => {},
            returnSelected: false,
            restricted: false,
            loadingBtn: true,
            isLoading: props.loading,
            disable: !isExactlyOneSelected,
        }
    ];

    const miscButtonClick = (id: string) => {
        switch(id) {
            case "add-work-entry":
                setNewEntry(true);
                setSelectedWorkEntry(undefined);
                setOpenModifyDrawer(true);
                break;
            case "edit-work-entry": {
                const selectedWorkEntryRow = (workEntriesData || []).find(
                    (entry: WorkEntryGridRow) =>
                        (entry.eventId ?? entry.eventId) === selectedRows[0],
                );
                if (!selectedWorkEntryRow) {
                    break;
                }

                setNewEntry(false);
                setSelectedWorkEntry({
                    id: selectedWorkEntryRow.id ?? selectedWorkEntryRow.eventId,
                    workDate: toDate(selectedWorkEntryRow.workDate),
                    payDate: toDate(selectedWorkEntryRow.payDate),
                    projectKey: selectedWorkEntryRow.projectKey ?? props.projectId,
                    rev_Rec_Date: toDate(selectedWorkEntryRow.revRecDate ?? selectedWorkEntryRow.rev_rec_date),
                    eventName: selectedWorkEntryRow.eventName ?? "",
                    EventNotes: selectedWorkEntryRow.eventNotes ?? selectedWorkEntryRow.EventNotes ?? "",
                });
                setOpenModifyDrawer(true);
                break;
            }
            case "delete-work-entry": {                

                deleteWorkEntry(selectedRows[0] as string)
                    .unwrap()
                    .then(() => {
                        const evtData: AccessEventOFCReducedDetails = {
                            sessionID: props.sessionId,
                            projectCode: props.projectCode,
                        };
                        logEvent("WorkEntryDeleted", evtData);

                        props.onShowSnackbar("Work Entry Deleted", "success");
                    })
                    .catch((error) => {
                        console.error("Failed to delete work entry:", error);

                        props.onShowSnackbar("Failed to delete Work Entry", "error");
                    });
                break;
            }
            case "view-trainer-entry":
                props.displayWorkEntryDetails(selectedRows[0] as number);
                break;
            default:
                break;
        }
    }

    return (
        <Box sx={{height: 1}}>
            {openModifyDrawer && (
                <ModifyWorkEntryDrawer
                    sessionId={props.sessionId}
                    projectId={props.projectId}
                    projectCode={props.projectCode}
                    projectName={props.projectName}
                    newEntry={newEntry}
                    workEntry={selectedWorkEntry}
                    revRecDate={props.revRecDate}
                    open={openModifyDrawer}
                    onClose={() => setOpenModifyDrawer(false)}
                    onShowSnackbar={props.onShowSnackbar}
                />
            )}

            <TrainerEntryDrawer
                sessionId={props.sessionId}
                projectCode={props.projectCode}
                open={openTrainerDrawer}
                onClose={() => setOpenTrainerDrawer(false)}
            />

            <DataGridPremium 
                columns={WorkEntryColumns} 
                rows={workEntriesData || []}
                loading={workEntriesLoading}
                getRowId={(row) => row.eventId}
                checkboxSelection
                disableSelectionOnClick
                onSelectionModelChange={(newSelection: any) => setSelectedRows(newSelection as Array<string | number>)}
                components={{Toolbar: CclDataGridToolbar}}
                componentsProps={{
                    toolbar: {
                        miscButtons: workEntryButtons,
                        btnClick: miscButtonClick,
                        hideSettingsButtons: true
                    }
                }}
                autoHeight 
            />
        </Box>                
    );
};

export default WorkEntryDataGrid;
